import ModalLayout from "@/components/layout/ModalLayout";
import ModalHeader from "@/components/ui/ModalHeader";
import { useState, useEffect } from "react";
import ToggleButton from "@/components/ui/ToggleButton";
import SelectBox from "@/components/ui/SelectBox";
import { useCodeList, useCodeListByPriceExtraCode } from "@/hooks/standard/st_code/useCode";
import { InputBox } from "@/components/ui/InputBox";
import { NumberInputBox } from "@/components/ui/NumberInputBox";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import { usePriceUnitAndExtraUpdate } from "@/hooks/standard/st_price_code/usePriceCode";

interface UpdateModalProps {
    arrivalCodeList: {
        codeId: number;
        name: string;
    }[];
    arrivalCode: string;
    departureCode: string;
    companyCode: number;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    editData?: {
        unitCodeData?: any; // 선택된 단가비 데이터
        extraCodeData?: any; // 선택된 추가비용 데이터
    };
}

export default function UpdateModal({ arrivalCodeList, arrivalCode, departureCode, companyCode, isOpen, onClose, onSave, editData }: UpdateModalProps) {
    const [isUnitToggleOn, setIsUnitToggleOn] = useState(true);
    const [isExtraToggleOn, setIsExtraToggleOn] = useState(false);
    const unitAndExtraUpdate = usePriceUnitAndExtraUpdate();
    const [arrival, setArrival] = useState("");
    const [departure, setDeparture] = useState("");
    const [distance, setDistance] = useState("");
    const [weight, setWeight] = useState("");
    const [price, setPrice] = useState("");
    const [weightRows, setWeightRows] = useState<{ weight: string; price: string }[]>([]);
    const [extraCode, setExtraCode] = useState("");
    const [extraPrice, setExtraPrice] = useState("");
    const [remark, setRemark] = useState("");
    const { data: extraCodeList } = useCodeListByPriceExtraCode();
    const { data: departureList } = useCodeList({
        parentId: Number(arrival) ?? undefined,
    }, {
        enabled: !!arrival
    });

    // 편집 데이터가 변경되면 폼 초기화
    useEffect(() => {
        if (editData?.unitCodeData) {
            const unitData = editData.unitCodeData;
            setIsUnitToggleOn(true);
            setIsExtraToggleOn(false);
            
            setArrival(arrivalCode.toString() || "");
            setDeparture(departureCode.toString() || "");
            setDistance(unitData.stDist?.toString() || "");
            
            const weights: { weight: string; price: string }[] = [];
            
            const weightKeys = Object.keys(unitData)
                .filter(key => 
                    key !== 'stName' && 
                    key !== 'stDist' && 
                    key !== 'name' && 
                    key !== 'dist' && 
                    key !== 'stPriceUnitCode'
                )
                .filter(key => {
                    const numericKey = parseFloat(key);
                    return !isNaN(numericKey) && unitData[key] != null;
                })
                .sort((a, b) => {
                    const numA = parseFloat(a);
                    const numB = parseFloat(b);
                    return numA - numB;
                });
            
            weightKeys.forEach((key, index) => {
                const formattedWeight = parseFloat(key).toString();
                
                if (index === 0) {
                    setWeight(formattedWeight);
                    setPrice(unitData[key]?.toString() || "");
                } else {
                    weights.push({ 
                        weight: formattedWeight, 
                        price: unitData[key]?.toString() || "" 
                    });
                }
            });
            
            setWeightRows(weights);
        } else {
            setIsUnitToggleOn(false);
        }

        if (editData?.extraCodeData) {
            const extraData = editData.extraCodeData;
            setIsExtraToggleOn(true);
            setIsUnitToggleOn(false);
            setExtraCode(extraData.stPriceExtraCode?.toString() || "");
            setExtraPrice(extraData.stExtraPrice?.toString() || "");
            setRemark(extraData.memo || "");
        } else {
            setIsExtraToggleOn(false);
        }
    }, [editData, companyCode]); // companyCode 의존성 추가

    const createWeight = () => {
        if (weightRows.length >= 9) {
            toast.warning("무게는 최대 10개까지 추가할 수 있습니다.");
            return;
        }
        if (!isUnitToggleOn) return;
        setWeightRows((prev) => [
            ...prev,
            { weight: "", price: "" },
        ]);
    };

    const handleCancel = () => {
        onClose();
        setIsUnitToggleOn(true);
        setWeightRows([]);
        setIsExtraToggleOn(false);
        setArrival("");
        setDeparture("");
        setDistance("");
        setWeight("");
        setPrice("");
        setExtraCode("");
        setExtraPrice("");
        setRemark("");
    };

    const handleSave = async () => {
        if (isUnitToggleOn) {
            if (!arrival || !departure || !weight || !price) {
                toast.error("출발지, 도착지, 무게, 금액을 모두 입력해주세요.");
                return;
            }

            for (const row of weightRows) {
                if (!row.weight || !row.price) {
                    toast.error("무게와 금액을 모두 입력해주세요.");
                    return;
                }
            }

            const weights = [
                weight,
                ...weightRows.map(r => r.weight)
            ].filter(w => w);
        
            const uniqueWeights = new Set(weights);
            if (uniqueWeights.size !== weights.length) {
                toast.error("같은 무게 값이 두 번 이상 입력되었습니다.");
                return;
            }
        }

        if (isExtraToggleOn) {
            if (!extraCode || !extraPrice) {
                toast.error("추가 유형 및 비용을 모두 입력해주세요.");
                return;
            }
        }

        try {
            const unitCodeRows: Array<{
                stPriceUnitCode: number;
                stDist: string;
                stWeight: number;
                stPrice: number;
            }> = [];

            if (isUnitToggleOn) {
                if (weight && price) {
                    unitCodeRows.push({
                        stPriceUnitCode: Number(departure),
                        stDist: distance && distance.trim() ? distance.trim() : "0",
                        stWeight: Number(weight),
                        stPrice: Number(price),
                    });
                }

                for (const row of weightRows) {
                    if (row.weight && row.price) {
                        unitCodeRows.push({
                            stPriceUnitCode: Number(departure),
                            stDist: distance && distance.trim() ? distance.trim() : "0",
                            stWeight: Number(row.weight),
                            stPrice: Number(row.price),
                        });
                    }
                }
            }

            const extraPayload = isExtraToggleOn
                ? {
                      stPriceExtraCode: Number(extraCode),
                      stParentCode: Number(companyCode),
                      stExtraPrice: Number(extraPrice),
                      memo: remark,
                  }
                : null;

            await unitAndExtraUpdate.mutateAsync({
                priceUnitCodes: isUnitToggleOn ? unitCodeRows : [],
                extraCode: extraPayload,
            });

            handleCancel();
            onSave();
        } catch (error) {
            console.log(error);
            return;
        }
    };

    return (
        <ModalLayout isOpen={isOpen} onClose={handleCancel} title="단가코드 수정">
            <ModalHeader
                title="단가비"
            />
            <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="space-y-4">
                    <SelectBox
                        required
                        disabled={true}
                        label="출발지"
                        value={arrival}
                        onChange={value => setArrival(value as string)}
                        options={arrivalCodeList.map((item: any) => ({
                            value: item.codeId.toString(), // number를 string으로 변환
                            label: item.name,
                        }))}
                    />
                </div>
                <div className="space-y-4">
                    <SelectBox
                        required
                        disabled={true}
                        label="도착지"
                        value={departure}
                        onChange={value => setDeparture(value as string)}
                        options={departureList?.map((item: any) => ({
                            value: item.codeId.toString(), // number를 string으로 변환
                            label: item.name,
                        })) ?? []}
                    />
                </div>
            </div>
            <div className="flex flex-col min-h-0">
                <div className="border-b border-[#bbbbbb] mb-4">
                    <div className="flex items-center justify-between">
                        <div className="flex justify-start">
                            <p className="text-base font-semibold mb-[6px] ">거리/무게/금액</p>
                        </div>
                        <Button
                                variant="add"
                                className="mb-[6px]"
                                onClick={createWeight}
                            >
                                추가
                            </Button>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="space-y-4">
                        <NumberInputBox
                            disabled={!isUnitToggleOn}
                            label="거리(km)"
                            value={distance}
                            onChange={e => setDistance(e.target.value)}
                            min={0}
                        />
                    </div>
                    <div className="space-y-4">
                        <NumberInputBox
                            disabled={!isUnitToggleOn}
                            label="무게(톤)"
                            value={weight}
                            onChange={e => setWeight(e.target.value)}
                            min={0}
                        />
                    </div>
                    <div className="space-y-4">
                        <NumberInputBox
                            disabled={!isUnitToggleOn}
                            label="금액"
                            value={price}
                            onChange={e => setPrice(e.target.value)}
                            min={0}
                        />
                    </div>
                </div>
                {weightRows.map((row, index) => (
                    <div key={index} className="grid grid-cols-3 gap-3 mb-4">
                        <div className="space-y-4 col-start-2">
                            <NumberInputBox
                                label="무게"
                                disabled={!isUnitToggleOn}
                                value={row.weight}
                                onChange={e =>
                                    setWeightRows(prev =>
                                        prev.map((item, i) =>
                                            i === index ? { ...item, weight: e.target.value } : item
                                        )
                                    )
                                }
                                min={0}
                            />
                        </div>
                        <div className="space-y-4">
                            <NumberInputBox
                                disabled={!isUnitToggleOn}
                                label="금액"
                                value={row.price}
                                onChange={e =>
                                    setWeightRows(prev =>
                                        prev.map((item, i) =>
                                            i === index ? { ...item, price: e.target.value } : item
                                        )
                                    )
                                }
                                min={0}
                            />
                        </div>
                    </div>
                ))}
            </div>
            <ModalHeader
                title="추가 비용"
            />
            <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="space-y-4">
                    <SelectBox
                        disabled={true}
                        label="추가"
                        value={extraCode}
                        onChange={value => setExtraCode(value as string)}
                        options={extraCodeList?.map((item: any) => ({
                            value: item.codeId.toString(), // number를 string으로 변환
                            label: item.name,
                        })) ?? []}
                        dropdownDirection="up"
                    />
                </div>
                <div className="space-y-4">
                    <NumberInputBox
                        disabled={!isExtraToggleOn}
                        label="비용"
                        value={extraPrice}
                        onChange={e => setExtraPrice(e.target.value)}
                    />
                </div>
            </div>
            <div className="flex flex-col min-h-0">
                <div className="gap-1 mb-4">
                    <div className="space-y-4">
                        <InputBox
                            disabled={!isExtraToggleOn}
                            label="비고"
                            value={remark}
                            onChange={e => setRemark(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
                <div className="flex justify-start w-full">
                    <Button variant="white" onClick={handleCancel} className="py-[10px]">
                        취소
                    </Button>
                </div>
                <div className="flex justify-end w-full">
                    <Button variant="primary" onClick={handleSave} className="py-[10px]">
                        수정
                    </Button>
                </div>
            </div>
        </ModalLayout>
    );
}